/**
 * ThinkingIndicator
 *
 * The assistant "working" indicator shown while a reasoning task runs. Two
 * phases, driven by `progressStatus`:
 *
 *  - Phase 1 (no progress yet): the nav Ahoy logo inside a brand-colour loader
 *    ring + an italic brand "Thinking..." label. The spinning ring is the
 *    liveness cue.
 *  - Phase 2 (progress streaming): the mark stays put and InlineReasoningCard
 *    renders the live status + reasoning steps beneath it.
 *
 * Extracted from three near-identical copies previously inlined in
 * ReasoningChat (which had drifted — one variant hid the avatar in phase 2)
 * so the behaviour and animation live in a single place.
 */
import React from 'react';

import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import { InlineReasoningCard } from './InlineReasoningCard';
import type { ReasoningStepData } from './ReasoningStepsList';

interface ThinkingIndicatorProps {
  /** Live status text from polling; an empty string means "no progress yet". */
  progressStatus: string;
  /** In-flight reasoning steps streamed from the backend. */
  partialSteps: ReasoningStepData[];
  /** Chain id for the inline card's expanded-state scoping (optional). */
  chainId?: string;
  /** Callback when a dynamic view is opened from a reasoning step. */
  onOpenDynamicViewSidebar?: (viewId: number) => void;
}

export const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({
  progressStatus,
  partialSteps,
  chainId,
  onOpenDynamicViewSidebar,
}) => {
  // The current loading text — the live status when we have one, else
  // "Thinking…" — renders BESIDE the loader as its label. The reasoning card
  // below only appears once actual steps stream in (so the status isn't
  // duplicated above it).
  const hasSteps = partialSteps.some(step => !step.is_final);

  return (
    <div className='space-y-3' role='status' aria-live='polite'>
      <LoadingIndicator
        type='ahoy'
        size='md'
        inline
        label={progressStatus || 'Thinking…'}
        labelClassName='text-[14px] font-medium italic leading-[20px] text-fg-brand-primary'
      />

      {hasSteps && (
        // Align the reasoning steps under the loader row: the ahoy `md` loader
        // (48px) + gap-3 puts the "Thinking…" label at x=60, while a step's own
        // 24px icon + gap-3 puts its title at x=36. A 24px (pl-6) indent closes
        // that gap so the label and the step titles read as one left-aligned
        // column beneath the loader.
        <div className='pl-6'>
          <InlineReasoningCard
            statusText={progressStatus}
            isProcessing
            steps={partialSteps}
            chainId={chainId}
            onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
          />
        </div>
      )}
    </div>
  );
};

export default ThinkingIndicator;
